import { useEffect, useState } from "react";
import { Input, InputChangeEvent } from "@progress/kendo-react-inputs";
import {
  DropDownList,
  DropDownListChangeEvent,
} from "@progress/kendo-react-dropdowns";
import { defaultProgramYear } from "@/utils/dates";
import { accessibleNoDataRender } from "@/components/shared/forms/kendoAccessibleNoData";

export type StaffSearchFormState = {
  programYear: string;
  seid: string;
  firstName: string;
  middleName: string;
  lastName: string;
  localStaffId: string;
};

type StaffSearchFormProps = {
  programYearOptions: string[];
  disabled: boolean;
  onSearch: (filters: StaffSearchFormState) => void;

  initialProgramYear?: string;
  initialLocalStaffId?: string;

  onClear?: () => void;
};

export function StaffSearchForm({
  programYearOptions,
  disabled,
  onSearch,
  initialProgramYear,
  initialLocalStaffId,
  onClear,
}: StaffSearchFormProps) {
  const [formState, setFormState] = useState<StaffSearchFormState>(() => ({
    programYear: defaultProgramYear(initialProgramYear),
    seid: "",
    firstName: "",
    middleName: "",
    lastName: "",
    localStaffId: initialLocalStaffId ?? "",
  }));

  useEffect(() => {
    setFormState((prev) => ({
      ...prev,
      programYear: defaultProgramYear(initialProgramYear),
      localStaffId: initialLocalStaffId ?? "",
    }));
  }, [initialProgramYear, initialLocalStaffId]);

  const handleChange = (key: keyof StaffSearchFormState, value: any) => {
    setFormState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSearchClick = () => {
    if (!disabled) {
      onSearch(formState);
    }
  };

  const handleClearClick = () => {
    const cleared: StaffSearchFormState = {
      programYear: defaultProgramYear(),
      seid: "",
      firstName: "",
      middleName: "",
      lastName: "",
      localStaffId: "",
    };

    setFormState(cleared);

    if (!disabled) {
      onSearch(cleared);
    }

    onClear?.();
  };

  return (
    <div className="mb-0 p-4 bg-white w-100 staff-search-form">
      <div className="row g-3">
        {/* Program Year */}
        <div className="col-12 col-md-4">
          <span
            id="staff-search-program-year-label"
            className="form-label fw-semibold"
          >
            <i className="bi bi-calendar me-1" aria-hidden="true"></i> Program
            Year
          </span>
          <DropDownList
            id="staff-search-program-year"
            ariaLabelledBy="staff-search-program-year-label"
            className="staff-search-control"
            style={{ width: "100%" }}
            data={programYearOptions}
            value={formState.programYear}
            onChange={(e: DropDownListChangeEvent) => {
              // Kendo's DropDownList can fire onChange with an empty/undefined
              // value during keyboard navigation (arrowing through the open
              // popup), before it settles on the actually-highlighted item.
              // Writing that straight into state blanks the displayed value
              // and it stays blank, since nothing re-derives it afterward.
              if (e.value) handleChange("programYear", e.value);
            }}
            disabled={disabled}
            listNoDataRender={accessibleNoDataRender(
              "No program years available"
            )}
          />
        </div>

        {/* SEID */}
        <div className="col-12 col-md-4">
          <label
            id="staff-search-seid-label"
            htmlFor="staff-search-seid"
            className="form-label fw-semibold"
          >
            <i className="bi bi-hash me-1" aria-hidden="true"></i> SEID
          </label>
          <Input
            id="staff-search-seid"
            ariaLabelledBy="staff-search-seid-label"
            className="staff-search-control"
            style={{ width: "100%" }}
            value={formState.seid}
            onChange={(e: InputChangeEvent) => handleChange("seid", e.value)}
            placeholder="Enter SEID"
            disabled={disabled}
          />
        </div>

        {/* Staff Local ID */}
        <div className="col-12 col-md-4">
          <label
            id="staff-search-local-id-label"
            htmlFor="staff-search-local-id"
            className="form-label fw-semibold"
          >
            <i className="bi bi-hash me-1" aria-hidden="true"></i> Staff Local
            ID
          </label>
          <Input
            id="staff-search-local-id"
            ariaLabelledBy="staff-search-local-id-label"
            className="staff-search-control"
            style={{ width: "100%" }}
            value={formState.localStaffId}
            onChange={(e: InputChangeEvent) =>
              handleChange("localStaffId", e.value)
            }
            placeholder="Enter staff local ID"
            disabled={disabled}
          />
        </div>

        {/* First Name */}
        <div className="col-12 col-md-4">
          <label
            id="staff-search-first-name-label"
            htmlFor="staff-search-first-name"
            className="form-label fw-semibold"
          >
            <i className="bi bi-person me-1" aria-hidden="true"></i> First Name
          </label>
          <Input
            id="staff-search-first-name"
            ariaLabelledBy="staff-search-first-name-label"
            className="staff-search-control"
            style={{ width: "100%" }}
            value={formState.firstName}
            onChange={(e: InputChangeEvent) =>
              handleChange("firstName", e.value)
            }
            placeholder="Enter first name"
            disabled={disabled}
          />
        </div>

        {/* Middle Name */}
        <div className="col-12 col-md-4">
          <label
            id="staff-search-middle-name-label"
            htmlFor="staff-search-middle-name"
            className="form-label fw-semibold"
          >
            <i className="bi bi-person me-1" aria-hidden="true"></i> Middle Name
          </label>
          <Input
            id="staff-search-middle-name"
            ariaLabelledBy="staff-search-middle-name-label"
            className="staff-search-control"
            style={{ width: "100%" }}
            value={formState.middleName}
            onChange={(e: InputChangeEvent) =>
              handleChange("middleName", e.value)
            }
            placeholder="Enter middle name"
            disabled={disabled}
          />
        </div>

        {/* Last Name */}
        <div className="col-12 col-md-4">
          <label
            id="staff-search-last-name-label"
            htmlFor="staff-search-last-name"
            className="form-label fw-semibold"
          >
            <i className="bi bi-person me-1" aria-hidden="true"></i> Last Name
          </label>
          <Input
            id="staff-search-last-name"
            ariaLabelledBy="staff-search-last-name-label"
            className="staff-search-control"
            style={{ width: "100%" }}
            value={formState.lastName}
            onChange={(e: InputChangeEvent) =>
              handleChange("lastName", e.value)
            }
            placeholder="Enter last name"
            disabled={disabled}
          />
        </div>

        {/* Buttons */}
        <div className="col-12 d-flex align-items-end gap-2 mt-4 mb-1">
          <button
            type="button"
            className="btn btn-primary d-flex align-items-center gap-2"
            onClick={handleSearchClick}
            disabled={disabled}
          >
            <i className="bi bi-search" aria-hidden="true"></i>
            Search
          </button>

          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleClearClick}
            disabled={disabled}
          >
            Clear
          </button>
        </div>
      </div>
    </div>
  );
}
